import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import api from '../services/api';
import { Search, MoreVertical, Tag, User, Clock, CheckCircle2, Circle, ChevronLeft, ChevronRight, ArrowUpDown, Users, BarChart2, FileUser, ClipboardList, Loader2, MessageSquare, MessageSquarePlus, Download } from 'lucide-react';
import { useParams, useLocation } from 'react-router-dom';
import { conversationsAPI, messagesAPI, complaintTicketsAPI } from '../services/api';
import AssignmentDialog from '../components/conversations/AssignmentDialog';
import LabelsDialog from '../components/conversations/LabelsDialog';
import RichTextEditor from '../components/conversations/RichTextEditor';
import VoiceRecorder from '../components/conversations/VoiceRecorder';
import SpeechToTextDialog from '../components/conversations/SpeechToTextDialog';
import SignatureDialog from '../components/conversations/SignatureDialog';
import ParticipantsDialog from '../components/conversations/ParticipantsDialog';
import ConversationMetrics from '../components/conversations/ConversationMetrics';
import CoPilotPanel from '../components/CoPilot/CoPilotPanel';
import CoPilotToggle from '../components/CoPilot/CoPilotToggle';
import ConversationTicketBadge from '../components/conversations/ConversationTicketBadge';
import { formatMessageTime, formatConversationTime } from '../utils/dateUtils';
import {
  buildExportRows,
  exportConversations,
  type ExportFormat,
  type ExportConversation,
  type ExportMessage
} from '../utils/conversationExport';
import { AIConversationControls, EscalationBanner, AIMessageBubble } from '../components/AI';
import { ConverseChatHeaderCalling } from '../components/Calling/ConverseChatHeaderIntegration';
import { MarkdownRenderer } from '../components/MarkdownRenderer';
import { usePermission } from '../hooks/usePermission'; // 14/11/2025 - User Access Management Module Phase 5.3.1
import { useSelector } from 'react-redux';
// Phase 5: WhatsApp Session Management UI (2026-01-19)
import { TemplateSuggestor, ConversationInitiator } from '../components/conversations';
import EmailTemplateSuggestor from '../components/conversations/EmailTemplateSuggestor';

import { cn } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '../components/ui/card';
import { API_ORIGIN } from '../config/api';

// ── Interfaces ──────────────────────────────────────────────────────────────

interface Conversation {
  id: number;
  display_id: number;
  created_at: string;
  contact: {
    id: number;
    name: string;
    email?: string;
    phone_number?: string;
    avatar_url?: string;
    custom_attributes?: Record<string, any>;
  };
  status: 'open' | 'pending' | 'resolved';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  last_activity_at: string;
  ai_enabled: boolean;
  assignee?: {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    avatar_url?: string;
  };
  unread_count: number;
  labels: Array<{
    id: number;
    title: string;
    color: string;
    description?: string;
  }>;
  channel: {
    id: number;
    name: string;
    channel_type: string;
  };
  escalated?: boolean;
  escalation_reason?: string;
  escalation_trigger?: string;
  escalated_at?: string;
  escalated_by?: string;
}

interface Attachment {
  id: number;
  file_type: string;
  file_url: string;
  thumb_url?: string;
  file_size?: number;
  direction: string;
  whatsapp_media_id?: string;
  mime_type?: string;
}

interface Message {
  id: number;
  content: string;
  message_type: 'incoming' | 'outgoing' | 'activity';
  content_type?: 'text' | 'file' | 'image' | 'audio' | 'video';
  sender?: {
    name: string;
    avatar_url?: string;
  };
  sender_type?: 'user' | 'agent' | 'ai' | 'bot';
  created_at: string;
  private: boolean;
  content_attributes?: {
    filename?: string;
    file_size?: number;
    file_type?: string;
    file_url?: string;
    mime_type?: string;
  };
  attachments?: Attachment[];
  ai_confidence?: number;
  tools_used?: string[];
  response_time_ms?: number;
  streaming?: boolean;
  metadata?: {
    whatsapp?: any;
    button?: {
      payload?: string;
      text?: string;
      context?: any;
      type?: string;
    };
    interactive?: any;
  };
}

// ── Component ────────────────────────────────────────────────────────────────

const ConversationsPage: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const authUser = useSelector((state: any) => state.auth.user);

  const hasViewAll = usePermission('conversations:view_all');
  const hasViewTeam = usePermission('conversations:view_team');
  const hasViewAssigned = usePermission('conversations:view_assigned');
  const canAssign = usePermission('conversations:assign');

  const [selectedTab, setSelectedTab] = useState(0);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messageText, setMessageText] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<{
    id: number;
    name: string;
    variables: Record<string, string>;
  } | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [labelsDialogOpen, setLabelsDialogOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sortBy, setSortBy] = useState('last_activity_at');
  const [uploading, setUploading] = useState(false);
  const [voiceRecorderOpen, setVoiceRecorderOpen] = useState(false);
  const [speechToTextDialogOpen, setSpeechToTextDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [userSignature, setUserSignature] = useState('');
  const [signatureEnabled, setSignatureEnabled] = useState(false);
  const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [metricsDialogOpen, setMetricsDialogOpen] = useState(false);
  const [showPreChatData, setShowPreChatData] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [conversationCounts, setConversationCounts] = useState({
    mine: 0,
    unassigned: 0,
    all: 0,
    team: 0
  });
  const [lastProcessedMessageId] = useState<number>(0);

  // Co-Pilot state
  const [copilotEnabled, setCopilotEnabled] = useState(false);

  // Digital Companion — Issued Tickets (feature-flagged)
  const [ticketingEnabled, setTicketingEnabled] = useState(false);

  // Phase 5: WhatsApp Session Management UI (2026-01-19)
  const [conversationInitiatorOpen, setConversationInitiatorOpen] = useState(false);
  const [templateSuggestorRefreshKey, setTemplateSuggestorRefreshKey] = useState(0);

  // Pagination state for messages
  const [, setCurrentPage] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const previousMessageCountRef = useRef(0);
  const firstMessageIdRef = useRef<number | null>(null);

  // Build available tabs based on permissions
  const availableTabs = React.useMemo(() => {
    const tabs: Array<{ label: string; value: string; scope: string }> = [];

    if (hasViewAssigned || hasViewTeam || hasViewAll) {
      tabs.push({ label: `Mine (${conversationCounts.mine})`, value: 'assigned', scope: 'assigned' });
    }
    if (hasViewTeam || hasViewAll) {
      tabs.push({ label: `Team (${conversationCounts.team})`, value: 'team', scope: 'team' });
    }
    if (hasViewAll) {
      tabs.push({ label: `All (${conversationCounts.all})`, value: 'all', scope: 'all' });
    }
    if (canAssign) {
      tabs.push({ label: `Unassigned (${conversationCounts.unassigned})`, value: 'unassigned', scope: 'unassigned' });
    }
    return tabs;
  }, [hasViewAll, hasViewTeam, hasViewAssigned, canAssign, conversationCounts]);

  // Digital Companion feature flag + deep link (?view=tickets&ticket=TKT-…)
  // Re-check whenever Conversations is shown again — Settings can flip the flag
  // while this page stayed mounted.
  useEffect(() => {
    let cancelled = false;

    const refreshTicketingFlag = () => {
      complaintTicketsAPI
        .isEnabled()
        .then((res) => {
          if (cancelled) return;
          const on = Boolean(res?.enabled);
          setTicketingEnabled(on);
        })
        .catch(() => {
          if (!cancelled) {
            setTicketingEnabled(false);
          }
        });
    };

    refreshTicketingFlag();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshTicketingFlag();
    };
    const onFeatureFlagsChanged = () => refreshTicketingFlag();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('ticketing-feature-flags-changed', onFeatureFlagsChanged);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('ticketing-feature-flags-changed', onFeatureFlagsChanged);
    };
  }, [location.pathname]);

  // v1 auto-scroll
  useEffect(() => {
    const currentCount = messages.length;
    const previousCount = previousMessageCountRef.current;
    const firstMessageId = messages.length > 0 ? messages[0].id : null;
    const previousFirstMessageId = firstMessageIdRef.current;

    const messagesAddedToEnd = currentCount > previousCount &&
                               firstMessageId === previousFirstMessageId &&
                               !loadingOlderMessages;

    if (messagesAddedToEnd && shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    previousMessageCountRef.current = currentCount;
    firstMessageIdRef.current = firstMessageId;
  }, [messages, loadingOlderMessages, shouldAutoScroll]);

  // v1 scroll handler
  const handleMessagesScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 100;
    setShouldAutoScroll(isAtBottom);

    if (element.scrollTop < 50 && hasMoreMessages && !loadingOlderMessages) {
      const previousScrollHeight = element.scrollHeight;
      const previousScrollTop = element.scrollTop;

      handleLoadOlderMessages().then(() => {
        if (messagesContainerRef.current) {
          const newScrollHeight = messagesContainerRef.current.scrollHeight;
          messagesContainerRef.current.scrollTop = previousScrollTop + (newScrollHeight - previousScrollHeight);
        }
      });
    }
  };

  // Load user signature settings
  useEffect(() => {
    const savedSignature = localStorage.getItem('userSignature') || '';
    const savedSignatureEnabled = localStorage.getItem('signatureEnabled') === 'true';
    setUserSignature(savedSignature);
    setSignatureEnabled(savedSignatureEnabled);
  }, []);

  // Load participants when conversation changes
  useEffect(() => {
    if (selectedConversation) {
      const loadParticipants = async () => {
        try {
          const response = await conversationsAPI.getParticipants(selectedConversation.id);
          setParticipants(response.participants || []);
        } catch (err) {
          console.error('Failed to load participants:', err);
          setParticipants([]);
        }
      };
      loadParticipants();
    }
  }, [selectedConversation]);

  // Load conversations from API
  // 14/11/2025 - User Access Management Module Phase 5.3.1
  const loadConversations = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = { per_page: 100 };
      const currentTab = availableTabs[selectedTab];
      if (currentTab && currentTab.scope !== 'all') { params.scope = currentTab.scope; }

      const response = await conversationsAPI.getConversations(params);
      setConversations(response.conversations || []);

      // Update conversation counts — use response.total so counts match backend totals.
      const countParams: any = { per_page: 1 };
      if (filterStatus !== 'all') { countParams.status = filterStatus; }

      const [allCountRes, mineResponse, teamResponse, unassignedResponse] = await Promise.all([
        conversationsAPI.getConversations({ ...countParams }),
        conversationsAPI.getConversations({ ...countParams, scope: 'assigned' }),
        (hasViewTeam || hasViewAll)
          ? conversationsAPI.getConversations({ ...countParams, scope: 'team' })
          : Promise.resolve({ total: 0 }),
        canAssign
          ? conversationsAPI.getConversations({ ...countParams, scope: 'unassigned' })
          : Promise.resolve({ total: 0 }),
      ]);

      setConversationCounts({
        mine: mineResponse.total ?? 0,
        unassigned: unassignedResponse.total ?? 0,
        all: allCountRes.total ?? 0,
        team: teamResponse.total ?? 0,
      });

      if (id && response.conversations) {
        const conv = response.conversations.find((c: Conversation) => c.id === parseInt(id));
        setSelectedConversation(conv || null);
      }
    } catch (err) {
      console.error('Failed to load conversations:', err);
      setError('Failed to load conversations');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  // Load conversations on mount and when filters change
  useEffect(() => {
    loadConversations();

    const pollInterval = setInterval(async () => {
      try {
        const params: any = { per_page: 100 };
        if (filterStatus !== 'all') {
          params.status = filterStatus;
        }

        const currentTab = availableTabs[selectedTab];
        if (currentTab && currentTab.scope !== 'all') { params.scope = currentTab.scope; }

        const response = await conversationsAPI.getConversations(params);
        setConversations(response.conversations || []);

        // 14/11/2025 - User Access Management Module Phase 5.3.1
        // Update conversation counts using response.total so counts stay in sync and update on poll.
        const countParams: any = { per_page: 1 };
        if (filterStatus !== 'all') {
          countParams.status = filterStatus;
        }

        const [allResponse, mineResponse, teamResponse, unassignedResponse] = await Promise.all([
          conversationsAPI.getConversations({ ...countParams }),
          conversationsAPI.getConversations({ ...countParams, scope: 'assigned' }),
          (hasViewTeam || hasViewAll)
            ? conversationsAPI.getConversations({ ...countParams, scope: 'team' })
            : Promise.resolve({ total: 0 }),
          canAssign
            ? conversationsAPI.getConversations({ ...countParams, scope: 'unassigned' })
            : Promise.resolve({ total: 0 })
        ]);

        setConversationCounts({
          mine: mineResponse.total ?? 0,
          unassigned: unassignedResponse.total ?? 0,
          all: allResponse.total ?? 0,
          team: teamResponse.total ?? 0
        });
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [id, filterStatus, selectedTab]);

  // Extract loadMessages function to be reusable
  const loadMessages = useCallback(async () => {
    if (!selectedConversation) return;

    try {
      const initialResponse = await messagesAPI.getMessages(selectedConversation.id, { page: 1, per_page: 1 });
      const total = initialResponse.total || 0;
      setTotalMessages(total);

      if (total <= 50) {
        const response = await messagesAPI.getMessages(selectedConversation.id, { page: 1, per_page: 50 });
        setCurrentPage(1);
        setHasMoreMessages(false);
        setMessages(response.messages || []);
        return;
      }

      const messagesPerPage = 50;
      const toLoad = 50;
      const skip = total - toLoad;
      const startPage = Math.floor(skip / messagesPerPage) + 1;
      const lastPage = Math.ceil(total / messagesPerPage);

      const allMessages = [];
      for (let page = startPage; page <= lastPage; page++) {
        const response = await messagesAPI.getMessages(selectedConversation.id, { page, per_page: 50 });
        allMessages.push(...(response.messages || []));
      }

      const skipWithinFirstPage = skip % messagesPerPage;
      const finalMessages = allMessages.slice(skipWithinFirstPage, skipWithinFirstPage + toLoad);

      setCurrentPage(startPage);
      setHasMoreMessages(skip > 0);
      setMessages(finalMessages);
    } catch (err) {
      console.error('Failed to load messages:', err);
      setMessages([]);
    }
  }, [selectedConversation]);

  // Load messages for selected conversation with v1 smart pagination
  useEffect(() => {
    if (selectedConversation) {
      setSelectedTemplate(null);
      setMessageText('');
      setMessages([]);
      setTotalMessages(0);
      setHasMoreMessages(false);

      loadMessages();

      const pollInterval = setInterval(loadMessages, 3000);
      return () => clearInterval(pollInterval);
    }
  }, [selectedConversation, lastProcessedMessageId]);

  // Handle send message — supports both template and regular text
  const handleSendMessage = async () => {
    if (!selectedConversation) return;

    if (selectedTemplate) {
      try {
        const response = await api.post(`/whatsapp/templates/${selectedTemplate.id}/send`, {
          conversation_id: selectedConversation.id,
          variables: selectedTemplate.variables,
          channel_id: selectedConversation.channel.id
        });

        if (response.data.success) {
          setSelectedTemplate(null);
          setMessageText('');
          loadMessages();
          setConversations(prev =>
            prev.map(conv =>
              conv.id === selectedConversation.id
                ? { ...conv, last_activity_at: new Date().toISOString() }
                : conv
            )
          );
          setTemplateSuggestorRefreshKey(k => k + 1);
        } else {
          throw new Error(response.data.error || 'Failed to send template');
        }
      } catch (err: any) {
        console.error('Failed to send template:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to send template');
        alert(err.response?.data?.detail || err.message || 'Failed to send template');
      }
      return;
    }

    if (messageText.trim()) {
      try {
        let messageContent = messageText;
        if (signatureEnabled && userSignature.trim()) {
          messageContent = messageText + '\n\n' + userSignature;
        }

        const response = await messagesAPI.sendMessage(selectedConversation.id, {
          content: messageContent,
          message_type: 'outgoing',
          private: false
        });

        setMessages(prev => [...prev, response]);
        setMessageText('');
        setConversations(prev =>
          prev.map(conv =>
            conv.id === selectedConversation.id
              ? { ...conv, last_activity_at: new Date().toISOString() }
              : conv
          )
        );
        setTemplateSuggestorRefreshKey(k => k + 1);
      } catch (err) {
        console.error('Failed to send message:', err);
        setError('Failed to send message');
      }
    }
  };

  const handleMessageTextChange = (text: string) => {
    setMessageText(text);
    if (selectedTemplate && text !== messageText) {
      setSelectedTemplate(null);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!selectedConversation) return;
    try {
      await conversationsAPI.toggleStatus(selectedConversation.id, status);
      setSelectedConversation(prev => prev ? { ...prev, status: status as any } : null);
      setConversations(prev =>
        prev.map(conv =>
          conv.id === selectedConversation.id ? { ...conv, status: status as any } : conv
        )
      );
    } catch (err) {
      console.error('Failed to change status:', err);
      setError('Failed to change conversation status');
    }
  };

  const handleAssignmentComplete = () => {
    // Reload conversations to get updated assignment info (same params as main list, including per_page)
    const reloadConversations = async () => {
      try {
        const params: any = { per_page: 100 };
        if (filterStatus !== 'all') params.status = filterStatus;
        const currentTab = availableTabs[selectedTab];
        if (currentTab && currentTab.scope !== 'all') params.scope = currentTab.scope;

        const response = await conversationsAPI.getConversations(params);
        setConversations(response.conversations || []);

        if (selectedConversation && response.conversations) {
          const updatedConv = response.conversations.find((c: Conversation) => c.id === selectedConversation.id);
          if (updatedConv) setSelectedConversation(updatedConv);
        }
      } catch (err) {
        console.error('Failed to reload conversations:', err);
      }
    };

    reloadConversations();
  };

  const handleLabelsUpdated = () => { handleAssignmentComplete(); };

  // Fetch all messages for a conversation (paginated)
  const fetchAllMessagesForConversation = useCallback(async (conversationId: number): Promise<Message[]> => {
    const perPage = 100;
    const first = await messagesAPI.getMessages(conversationId, { page: 1, per_page: perPage });
    const total = first.total ?? 0;
    const allMessages: Message[] = [...(first.messages || [])];
    if (total <= perPage) return allMessages;
    const totalPages = Math.ceil(total / perPage);
    for (let page = 2; page <= totalPages; page++) {
      const res = await messagesAPI.getMessages(conversationId, { page, per_page: perPage });
      allMessages.push(...(res.messages || []));
    }
    return allMessages;
  }, []);

  const toExportConv = (c: Conversation): ExportConversation => ({
    id: c.id,
    display_id: c.display_id,
    status: c.status,
    contact: {
      id: c.contact.id,
      name: c.contact.name,
      email: c.contact.email,
      phone_number: c.contact.phone_number
    },
    assignee: c.assignee ? { id: c.assignee.id, full_name: c.assignee.full_name } : undefined,
    channel: c.channel ? { name: c.channel.name, channel_type: c.channel.channel_type } : undefined,
    created_at: c.created_at,
    last_activity_at: c.last_activity_at
  });

  const toExportMsg = (m: Message): ExportMessage => ({
    id: m.id,
    content: m.content,
    message_type: m.message_type,
    content_type: m.content_type,
    sender: m.sender,
    sender_type: m.sender_type,
    created_at: m.created_at,
    private: m.private
  });

  const handleDownloadCurrent = useCallback(async (format: ExportFormat) => {
    if (!selectedConversation) return;
    setExporting(true);
    try {
      const msgs = await fetchAllMessagesForConversation(selectedConversation.id);
      const rows = buildExportRows([{
        conversation: toExportConv(selectedConversation),
        messages: msgs.map(toExportMsg)
      }]);
      const name = `conversation-${selectedConversation.display_id}-${selectedConversation.contact?.name?.replace(/[^a-zA-Z0-9]/g, '_') || selectedConversation.id}`;
      exportConversations(rows, name, format);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [selectedConversation, fetchAllMessagesForConversation]);

  const handleDownloadAll = useCallback(async (format: ExportFormat) => {
    setExporting(true);
    try {
      const payloads: Array<{ conversation: ExportConversation; messages: ExportMessage[] }> = [];
      for (const conv of conversations) {
        const msgs = await fetchAllMessagesForConversation(conv.id);
        payloads.push({ conversation: toExportConv(conv), messages: msgs.map(toExportMsg) });
      }
      const rows = buildExportRows(payloads);
      const name = `conversations-export-${new Date().toISOString().slice(0, 10)}`;
      exportConversations(rows, name, format);
    } catch (err) {
      console.error('Export all failed:', err);
    } finally {
      setExporting(false);
    }
  }, [conversations, fetchAllMessagesForConversation]);

  const handleFileAttachment = async () => {
    if (!selectedConversation) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = false;
    input.accept = 'image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.xls';
    input.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (!file) return;
      try {
        setUploading(true);
        const response = await messagesAPI.uploadAttachment(selectedConversation.id, file, false);
        setMessages(prev => [...prev, response]);
        setConversations(prev =>
          prev.map(conv =>
            conv.id === selectedConversation.id
              ? { ...conv, last_activity_at: new Date().toISOString() }
              : conv
          )
        );
      } catch (err) {
        console.error('Failed to upload attachment:', err);
        setError('Failed to upload file');
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const handleVoiceRecording = async (audioBlob: Blob) => {
    if (!selectedConversation) return;
    try {
      setUploading(true);
      const audioFile = new File([audioBlob], 'voice-message.webm', { type: 'audio/webm' });
      const response = await messagesAPI.uploadAttachment(selectedConversation.id, audioFile, false);
      setMessages(prev => [...prev, response]);
      setConversations(prev =>
        prev.map(conv =>
          conv.id === selectedConversation.id
            ? { ...conv, last_activity_at: new Date().toISOString() }
            : conv
        )
      );
    } catch (err) {
      console.error('Failed to send voice message:', err);
      setError('Failed to send voice message');
    } finally {
      setUploading(false);
    }
  };

  const handleSpeechToTextComplete = (transcript: string) => {
    setMessageText(prevText => {
      const currentText = prevText.trim();
      return currentText ? `${currentText}\n\n${transcript}` : transcript;
    });
    setSpeechToTextDialogOpen(false);
  };

  const handleSignatureSave = (signature: string, enabled: boolean) => {
    setUserSignature(signature);
    setSignatureEnabled(enabled);
    localStorage.setItem('userSignature', signature);
    localStorage.setItem('signatureEnabled', enabled.toString());
  };

  const handleParticipantsUpdated = () => {
    if (selectedConversation) {
      const reload = async () => {
        try {
          const response = await conversationsAPI.getParticipants(selectedConversation.id);
          setParticipants(response.participants || []);
        } catch (err) {
          console.error('Failed to reload participants:', err);
        }
      };
      reload();
    }
    handleAssignmentComplete();
  };

  const isAiEnabled = selectedConversation?.ai_enabled || false;

  const handleLoadOlderMessages = async () => {
    if (!selectedConversation || loadingOlderMessages || !hasMoreMessages) return;
    setLoadingOlderMessages(true);
    try {
      const currentlyLoaded = messages.length;
      const alreadySkipped = totalMessages - currentlyLoaded;
      const toLoad = Math.min(50, alreadySkipped);
      const newSkip = alreadySkipped - toLoad;
      const page = Math.floor(newSkip / 50) + 1;
      const skipWithinPage = newSkip % 50;

      const response = await messagesAPI.getMessages(selectedConversation.id, { page, per_page: 50 });
      let olderMessages = response.messages || [];

      if (skipWithinPage > 0 || toLoad < 50) {
        olderMessages = olderMessages.slice(skipWithinPage, skipWithinPage + toLoad);
      }
      if (olderMessages.length > 0) {
        setMessages(prev => [...olderMessages, ...prev]);
        setCurrentPage(page);
        setHasMoreMessages(newSkip > 0);
      }
    } catch (err) {
      console.error('Failed to load older messages:', err);
    } finally {
      setLoadingOlderMessages(false);
    }
  };

  // ── UI helpers ───────────────────────────────────────────────────────────

  const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'secondary' | 'outline' => {
    switch (status) {
      case 'open':     return 'success';
      case 'pending':  return 'warning';
      case 'resolved': return 'secondary';
      default:         return 'outline';
    }
  };

  const getChannelIcon = (type: string) => {
    const iconStyle = { width: '14px', height: '14px', display: 'inline-block', verticalAlign: 'middle' };
    const getIconPath = (t: string) => {
      switch (t) {
        case 'whatsapp': return '/icons/whatsapp.svg';
        case 'email':    return '/icons/email.svg';
        case 'website':  return '/icons/website.svg';
        case 'api':      return '/icons/api.svg';
        case 'facebook': return '/icons/facebook.svg';
        case 'twitter':  return '/icons/twitter.svg';
        case 'instagram':return '/icons/instagram.svg';
        case 'telegram': return '/icons/telegram.svg';
        case 'sms':      return '/icons/sms.svg';
        case 'slack':    return '/icons/slack.svg';
        case 'custom_gpt':return '/icons/custom_gpt.svg';
        case 'line':     return '/icons/website.svg';
        default:         return '/icons/api.svg';
      }
    };
    return <img src={getIconPath(type)} alt={`${type} channel`} style={iconStyle} />;
  };

  const hasPreChatFormData = (conversation: Conversation) =>
    !!(conversation.contact.custom_attributes &&
       Object.keys(conversation.contact.custom_attributes).length > 0);

  const formatPreChatFormData = (customAttributes: Record<string, any>) => {
    if (!customAttributes) return [];
    return Object.entries(customAttributes)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
        value: typeof value === 'object' ? JSON.stringify(value) : String(value)
      }));
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const renderAttachment = (message: Message) => {
    const getFileIcon = (type?: string) => {
      switch (type) {
        case 'document': return '📄';
        case 'audio': return '🎵';
        case 'video': return '🎥';
        case 'image': return '🖼️';
        default: return '📎';
      }
    };

    if (message.attachments && message.attachments.length > 0) {
      return (
        <div className="mt-2 space-y-1">
          {message.attachments.map((attachment) => {
            const { file_type, file_url, file_size } = attachment;
            const filename = file_url.split('/').pop() || 'file';

            if (file_type === 'image') {
              return (
                <div key={attachment.id} className="mb-1">
                  <img
                    src={`${API_ORIGIN}${file_url}`}
                    alt={filename}
                    className="max-w-[200px] max-h-[200px] rounded-lg cursor-pointer"
                    onClick={() => window.open(`${API_ORIGIN}${file_url}`, '_blank')}
                  />
                  <p className="text-xs text-muted-foreground mt-0.5">{filename} ({formatFileSize(file_size)})</p>
                </div>
              );
            }

            if (file_type === 'audio') {
              return (
                <div key={attachment.id} className="mb-1">
                  <audio controls className="w-64" src={`${API_ORIGIN}${file_url}`} />
                  <p className="text-xs text-muted-foreground mt-0.5">🎵 {filename} ({formatFileSize(file_size)})</p>
                </div>
              );
            }

            if (file_type === 'video') {
              return (
                <div key={attachment.id} className="mb-1">
                  <video controls className="max-w-[300px] max-h-[200px] rounded-lg" src={`${API_ORIGIN}${file_url}`} />
                  <p className="text-xs text-muted-foreground mt-0.5">🎥 {filename} ({formatFileSize(file_size)})</p>
                </div>
              );
            }

            return (
              <div key={attachment.id} className="mb-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 text-xs h-7"
                  onClick={() => window.open(`${API_ORIGIN}${file_url}`, '_blank')}
                >
                  <span>{getFileIcon(file_type)}</span>
                  {filename}
                </Button>
                <p className="text-xs text-muted-foreground mt-0.5">{formatFileSize(file_size)}</p>
              </div>
            );
          })}
        </div>
      );
    }

    // Fallback to old content_attributes system (backward compatibility)
    if (!message.content_attributes?.file_url || message.content_type === 'text') {
      return null;
    }

    const { filename, file_size, file_type, file_url } = message.content_attributes;

    if (message.content_type === 'image') {
      return (
        <div className="mt-2">
          <img
            src={`${API_ORIGIN}${file_url}`}
            alt={filename}
            className="max-w-[200px] max-h-[200px] rounded-lg cursor-pointer"
            onClick={() => window.open(`${API_ORIGIN}${file_url}`, '_blank')}
          />
          <p className="text-xs text-muted-foreground mt-0.5">{filename} ({formatFileSize(file_size)})</p>
        </div>
      );
    }

    if (message.content_type === 'audio') {
      return (
        <div className="mt-2">
          <audio controls className="w-64" src={`${API_ORIGIN}${file_url}`} />
          <p className="text-xs text-muted-foreground mt-0.5">🎵 {filename} ({formatFileSize(file_size)})</p>
        </div>
      );
    }

    if (message.content_type === 'video') {
      return (
        <div className="mt-2">
          <video controls className="max-w-[300px] max-h-[200px] rounded-lg" src={`${API_ORIGIN}${file_url}`} />
          <p className="text-xs text-muted-foreground mt-0.5">🎥 {filename} ({formatFileSize(file_size)})</p>
        </div>
      );
    }

    return (
      <div className="mt-2">
        <Button
          variant="outline"
          size="sm"
          className="gap-2 text-xs h-7"
          onClick={() => window.open(`${API_ORIGIN}${file_url}`, '_blank')}
        >
          <span>{getFileIcon(file_type)}</span>
          {filename}
        </Button>
        <p className="text-xs text-muted-foreground mt-0.5">{formatFileSize(file_size)}</p>
      </div>
    );
  };

  const renderButtonResponse = (message: Message) => {
    const buttonData = message.metadata?.button || message.metadata?.interactive;
    if (!buttonData) return null;
    const buttonText = buttonData.text || buttonData.title || buttonData.payload || 'Button clicked';
    return (
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="gap-1 text-xs">
          <ClipboardList className="h-3 w-3" />
          Button Response
        </Badge>
        <span className="text-sm font-medium">{buttonText}</span>
      </div>
    );
  };

  const filteredConversations = useMemo(() => {
    return conversations.filter(conv => {
      const matchesSearch = conv.contact.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                           conv.contact.email?.toLowerCase().includes(searchText.toLowerCase());
      const matchesStatus = filterStatus === 'all' || conv.status === filterStatus;
      return matchesSearch && matchesStatus;
    }).sort((a, b) => {
      let result = 0;
      switch (sortBy) {
        case 'last_activity_at':
          result = new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime(); break;
        case 'id':
          result = (b.id || 0) - (a.id || 0); break;
        case 'created_at':
          result = (b.created_at ? new Date(b.created_at).getTime() : 0) -
                   (a.created_at ? new Date(a.created_at).getTime() : 0); break;
        case 'contact_name':
          result = a.contact.name.localeCompare(b.contact.name); break;
        case 'status':
          result = a.status.localeCompare(b.status); break;
        case 'priority':
          const order: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
          result = (order[a.priority] ?? 999) - (order[b.priority] ?? 999); break;
        default:
          result = 0;
      }
      if (result === 0 && sortBy !== 'last_activity_at') {
        result = new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime();
      }
      return result;
    });
  }, [conversations, searchText, filterStatus, sortBy]);

  // ── Render ───────────────────────────────────────────────────────────────

  if (error && conversations.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center gap-3">
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" size="sm" onClick={loadConversations}>Retry</Button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative flex min-h-0 flex-1 overflow-hidden',
        // Cap height so SidebarInset does not scroll as a second layer; list + thread scroll inside.
        'max-h-[calc(100svh-4.5rem)] md:max-h-[calc(100svh-5.25rem)]',
      )}
    >
      {/* 2026-08-08 (§6.2): Issued Tickets is its own top-level page with a
          real sidebar entry (see DashboardLayout.tsx) — the embedded
          mainView === 'tickets' render branch that used to swap this whole
          view out was removed outright, and this in-page link-out button
          (redundant with the sidebar now) was removed too, along with the
          now-dead mainView/switchMainView/viewParam state that only existed
          to drive it. Per-conversation ticket linkage is still surfaced via
          ConversationTicketBadge in the thread header, which is not
          redundant — it's scoped to the open conversation, not a
          navigation link. */}

      {/* ── Left: Conversation List ─────────────────────────────────────── */}
      <Card className={cn(
        "flex min-h-0 flex-col shrink-0 rounded-none border-r border-border border-y-0 border-l-0 transition-[width] duration-300",
        sidebarCollapsed ? "w-16" : "w-[380px]"
      )}>
        <CardHeader className="flex flex-row items-center justify-between px-3 py-2 space-y-0">
          <h2 className="text-sm font-semibold tracking-tight">
            {!sidebarCollapsed && 'Conversations'}
          </h2>
          <div className="flex items-center gap-1">
            {!sidebarCollapsed && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {[
                    { value: 'id', label: 'Conversation ID' },
                    { value: 'last_activity_at', label: 'Last Activity' },
                    { value: 'created_at', label: 'Created Date' },
                    { value: 'contact_name', label: 'Contact Name' },
                    { value: 'status', label: 'Status' },
                    { value: 'priority', label: 'Priority' },
                  ].map((opt) => (
                    <DropdownMenuItem
                      key={opt.value}
                      onClick={() => setSortBy(opt.value)}
                      className={cn(sortBy === opt.value && "font-medium bg-accent")}
                    >
                      {opt.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
              {sidebarCollapsed
                ? <ChevronRight className="h-4 w-4" />
                : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>

        <Separator />

        {!sidebarCollapsed && (
          <>
            {/* Scope selector */}
            {availableTabs.length > 0 && (
              <>
                <div className="px-2 py-1.5">
                  <Select
                    value={String(selectedTab)}
                    onValueChange={(v) => setSelectedTab(Number(v))}
                  >
                    <SelectTrigger className="h-8 text-xs border-border/60 bg-muted/40 focus:ring-0 focus:ring-offset-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTabs.map((tab, index) => (
                        <SelectItem key={tab.value} value={String(index)} className="text-xs">
                          {tab.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Separator />
              </>
            )}

            {/* Search + Status filter */}
            <div className="px-2 pt-2 pb-2 space-y-1.5">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search conversations..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="pl-8 h-9 text-sm"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="snoozed">Snoozed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />
          </>
        )}

        {/* Conversation list */}
        <CardContent className="p-0 flex-1 min-h-0 overflow-hidden">
          <ScrollArea className="h-full min-h-0">
            {loading && conversations.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                <p className="text-sm text-muted-foreground">No conversations found</p>
              </div>
            ) : (
              <div className="p-2 space-y-1.5">
                {filteredConversations.map((conversation) => (
                  <button
                    key={conversation.id}
                    onClick={() => setSelectedConversation(conversation)}
                    className={cn(
                      "w-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg border border-border/50 bg-card text-left transition-all hover:bg-accent/50 hover:border-border hover:shadow-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring shadow-sm overflow-hidden",
                      selectedConversation?.id === conversation.id && "border-primary/40 bg-accent ring-1 ring-primary/20 shadow-none"
                    )}
                  >
                    {/* Avatar + unread badge */}
                    <div className="relative shrink-0 mt-0.5">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={conversation.contact.avatar_url || undefined} />
                        <AvatarFallback className="text-xs font-semibold bg-primary/10 text-primary">
                          {conversation.contact.name.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {conversation.unread_count > 0 && (
                        <span className="absolute -top-1 -right-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[8px] font-bold px-0.5">
                          {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
                        </span>
                      )}
                    </div>

                    {!sidebarCollapsed && (
                      <div className="flex-1 min-w-0 space-y-0.5">
                        {/* Row 1: Name + Time */}
                        <div className="flex items-center justify-between gap-1">
                          <p className={cn(
                            "text-sm truncate leading-tight",
                            conversation.unread_count > 0 ? "font-semibold" : "font-medium"
                          )}>
                            {conversation.contact.name}
                          </p>
                          <span className="text-[10px] text-muted-foreground shrink-0 leading-tight">
                            {formatConversationTime(conversation.last_activity_at)}
                          </span>
                        </div>
                        {/* Row 2: Channel · ID */}
                        <p className="text-xs text-muted-foreground truncate leading-tight">
                          {conversation.channel.name} · #{conversation.display_id}
                        </p>
                        {/* Row 3: Badges + channel icon */}
                        <div className="flex items-center gap-1 pt-0.5">
                          {conversation.priority === 'urgent' && (
                            <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4 font-normal rounded-full">
                              Urgent
                            </Badge>
                          )}
                          <Badge
                            variant={getStatusBadgeVariant(conversation.status)}
                            className="text-[10px] px-1.5 py-0 h-4 font-normal rounded-full capitalize"
                          >
                            {conversation.status}
                          </Badge>
                          {conversation.labels.slice(0, 1).map((label) => (
                            <span
                              key={label.id}
                              className="inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-medium leading-4 shrink-0"
                              style={{ backgroundColor: label.color, color: 'white' }}
                            >
                              {label.title}
                            </span>
                          ))}
                          <span className="ml-auto shrink-0 opacity-60">{getChannelIcon(conversation.channel.channel_type)}</span>
                        </div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* ── Right: Conversation Panel ─────────────────────────────────────── */}
      {selectedConversation ? (
        <div className="flex min-h-0 flex-1 min-w-0">

          {/* Main conversation Card */}
          <Card className={cn(
            "flex min-h-0 flex-col flex-1 min-w-0 rounded-none border-0",
            copilotEnabled && "border-r border-border rounded-none"
          )}>

            {/* ── Header: 2-row layout ──────────────────────────────────────── */}
            <div className="shrink-0 border-b border-border bg-card">

              {/* Row 1 — Avatar · Name · Actions */}
              <div className="flex items-center gap-3 px-4 py-2 min-w-0">

                {/* Avatar + status dot */}
                <div className="relative shrink-0">
                  <Avatar className="h-9 w-9 ring-2 ring-offset-1 ring-offset-background ring-border/60">
                    <AvatarImage src={selectedConversation.contact.avatar_url || undefined} />
                    <AvatarFallback className="text-sm font-semibold bg-primary/10 text-primary">
                      {selectedConversation.contact.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className={cn(
                    "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-background",
                    selectedConversation.status === 'open' ? "bg-emerald-500"
                    : selectedConversation.status === 'pending' ? "bg-amber-500"
                    : "bg-muted-foreground/40"
                  )} />
                </div>

                {/* Name + channel info — takes remaining space */}
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold truncate leading-tight">
                      {selectedConversation.contact.name}
                    </p>
                    <Badge
                      variant={getStatusBadgeVariant(selectedConversation.status)}
                      className="text-[10px] h-4 px-1.5 rounded-full font-normal capitalize shrink-0"
                    >
                      {selectedConversation.status}
                    </Badge>
                    <ConversationTicketBadge
                      conversationId={selectedConversation.id}
                      ticketingEnabled={ticketingEnabled}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground truncate leading-tight">
                    {selectedConversation.channel.name} · #{selectedConversation.display_id}
                  </p>
                </div>

                {/* Action buttons — always shrink-0, never push into text */}
                <div className="flex items-center shrink-0 gap-0.5">
                  {/* Assign */}
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={() => setAssignmentDialogOpen(true)} title={selectedConversation.assignee ? `Assigned: ${selectedConversation.assignee.full_name}` : 'Assign'}>
                    <User className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={() => setLabelsDialogOpen(true)} title="Labels">
                    <Tag className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={() => setParticipantsDialogOpen(true)} title="Participants">
                    <Users className="h-3.5 w-3.5" />
                  </Button>

                  <div className="w-px h-4 bg-border mx-0.5 shrink-0" />

                  <Button
                    variant="ghost" size="icon"
                    className={cn("h-7 w-7", selectedConversation.status === 'resolved' ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground hover:text-foreground")}
                    onClick={() => handleStatusChange('resolved')}
                    title={selectedConversation.status === 'resolved' ? 'Resolved' : 'Resolve'}>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={() => setMetricsDialogOpen(true)} title="Analytics">
                    <BarChart2 className="h-3.5 w-3.5" />
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        disabled={exporting} title="Download">
                        {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>This conversation</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleDownloadCurrent('csv')}>CSV</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDownloadCurrent('excel')}>Excel</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDownloadCurrent('json')}>JSON</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>All conversations</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleDownloadAll('csv')}>CSV</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDownloadAll('excel')}>Excel</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDownloadAll('json')}>JSON</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <AIConversationControls
                    conversationId={selectedConversation.id}
                    aiEnabled={isAiEnabled}
                    escalated={selectedConversation.escalated || false}
                    onAIToggle={(enabled) => {
                      setSelectedConversation(prev => prev ? { ...prev, ai_enabled: enabled } : null);
                      loadConversations();
                    }}
                    onEscalate={() => { loadConversations(); }}
                  />

                  <ConverseChatHeaderCalling
                    agentId={String(authUser?.id ?? 'agent')}
                    conversationId={selectedConversation.id.toString()}
                    customerInfo={{
                      customer_id: selectedConversation.contact.email ?? selectedConversation.contact.id.toString(),
                      name: selectedConversation.contact.name,
                      email: selectedConversation.contact.email
                    }}
                    conversationStatus={selectedConversation.status}
                    isHumanAgent={!isAiEnabled}
                  />

                  {hasPreChatFormData(selectedConversation) && (
                    <Button variant="ghost" size="icon"
                      className={cn("h-7 w-7", showPreChatData ? "text-primary" : "text-muted-foreground hover:text-foreground")}
                      onClick={() => setShowPreChatData(!showPreChatData)} title="Pre-chat Info">
                      <FileUser className="h-3.5 w-3.5" />
                    </Button>
                  )}

                  <div className="w-px h-4 bg-border mx-0.5 shrink-0" />

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleStatusChange('snoozed')}>
                        <Clock className="h-4 w-4 mr-2" /> Snooze
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleStatusChange('pending')}>
                        <Circle className="h-4 w-4 mr-2" /> Mark as pending
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleStatusChange('open')}>
                        <Circle className="h-4 w-4 mr-2" /> Reopen
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleStatusChange('resolved')}>
                        <CheckCircle2 className="h-4 w-4 mr-2" /> Resolve
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Row 2 — CoPilot tab toggle + AI status */}
              <div className="flex items-center justify-between border-t border-border/50 pl-1 pr-4 py-0.5">
                <CoPilotToggle enabled={copilotEnabled} onChange={setCopilotEnabled} />

                {/* AI / Human badge */}
                <div className={cn(
                  "flex items-center gap-1.5 text-xs px-2 py-1 rounded-full font-medium transition-colors",
                  selectedConversation.escalated
                    ? "bg-destructive/10 text-destructive"
                    : isAiEnabled
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-muted-foreground"
                )}>
                  <span className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    selectedConversation.escalated ? "bg-destructive animate-pulse"
                    : isAiEnabled ? "bg-primary animate-pulse"
                    : "bg-muted-foreground"
                  )} />
                  {selectedConversation.escalated ? 'Escalated'
                    : isAiEnabled ? 'AI Active'
                    : 'Human'}
                </div>
              </div>

            </div>

            {/* Escalation Banner */}
            {selectedConversation.escalated && (
              <div className="border-b border-border">
                <EscalationBanner
                  conversationId={selectedConversation.id}
                  escalated={selectedConversation.escalated}
                  escalationReason={selectedConversation.escalation_reason}
                  escalationTrigger={selectedConversation.escalation_trigger}
                  escalatedAt={selectedConversation.escalated_at ? new Date(selectedConversation.escalated_at) : undefined}
                  escalatedBy={selectedConversation.escalated_by}
                  assignedAgent={selectedConversation.assignee}
                  onResolve={() => { loadConversations(); }}
                  onToggleAI={() => {
                    setSelectedConversation(prev => prev ? { ...prev, ai_enabled: true } : null);
                    loadConversations();
                  }}
                />
              </div>
            )}

            {/* Pre-chat Form Data */}
            {showPreChatData && hasPreChatFormData(selectedConversation) && (
              <div className="px-4 py-3 border-b border-border bg-muted/20">
                <p className="text-xs font-medium flex items-center gap-1.5 mb-2 text-muted-foreground">
                  <FileUser className="h-3.5 w-3.5" />
                  Pre-chat Form Information
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {formatPreChatFormData(selectedConversation.contact.custom_attributes || {}).map((item) => (
                    <div key={item.key}>
                      <p className="text-xs text-muted-foreground font-medium">{item.label}</p>
                      <p className="text-sm mt-0.5 break-words">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CardContent — scrollable messages area */}
            <CardContent className="p-0 flex-1 min-h-0 overflow-hidden"
              style={{ background: 'linear-gradient(180deg, hsl(var(--muted)/0.15) 0%, hsl(var(--background)) 100%)' }}>
              <div
                ref={messagesContainerRef}
                onScroll={handleMessagesScroll}
                className="h-full min-h-0 overflow-y-auto px-4 py-3 space-y-2.5"
              >
                {loadingOlderMessages && (
                  <div className="flex justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                )}

                {messages.map((message) => {
                  // AI / bot messages
                  if (message.sender_type === 'ai' || message.sender_type === 'bot') {
                    return (
                      <AIMessageBubble
                        key={message.id}
                        content={message.content}
                        confidence={message.ai_confidence}
                        toolsUsed={message.tools_used}
                        responseTimeMs={message.response_time_ms}
                        isStreaming={message.streaming || false}
                        timestamp={new Date(message.created_at)}
                        showMetadata={true}
                      />
                    );
                  }

                  // Activity messages
                  if (message.message_type === 'activity') {
                    return (
                      <div key={message.id} className="flex items-center gap-3 my-1">
                        <div className="flex-1 h-px bg-border/60" />
                        <span className="text-[11px] text-muted-foreground bg-background border border-border/60 px-3 py-0.5 rounded-full shrink-0 shadow-sm">
                          {message.content}
                        </span>
                        <div className="flex-1 h-px bg-border/60" />
                      </div>
                    );
                  }

                  // Regular messages
                  const isOutgoing = message.message_type === 'outgoing';
                  const isPrivate = message.private;

                  return (
                    <div key={message.id}
                      className={cn("flex", isOutgoing ? "justify-end" : "justify-start")}>
                      <div className={cn(
                        "max-w-[75%] px-4 py-2.5",
                        isOutgoing
                          ? "bg-primary text-primary-foreground rounded-2xl rounded-tr-sm"
                          : isPrivate
                          ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-2xl rounded-tl-sm"
                          : "bg-muted rounded-2xl rounded-tl-sm"
                      )}>
                        {isPrivate && (
                          <p className="text-xs italic text-muted-foreground mb-1">Private Note</p>
                        )}
                        {message.metadata?.button || message.metadata?.interactive
                          ? renderButtonResponse(message)
                          : (
                            <MarkdownRenderer
                              content={message.content}
                              sx={{
                                fontSize: '0.875rem',
                                color: isOutgoing ? 'white' : 'inherit',
                                '& a': { color: isOutgoing ? 'white' : 'hsl(var(--primary))' },
                                '& code': {
                                  backgroundColor: isOutgoing
                                    ? 'rgba(255,255,255,0.2)'
                                    : 'rgba(0,0,0,0.05)'
                                },
                                '& pre': {
                                  backgroundColor: isOutgoing
                                    ? 'rgba(255,255,255,0.1)'
                                    : 'rgba(0,0,0,0.05)'
                                }
                              }}
                            />
                          )}
                        {renderAttachment(message)}
                        <p className={cn(
                          "text-xs mt-1",
                          isOutgoing ? "text-primary-foreground/70" : "text-muted-foreground"
                        )}>
                          {formatMessageTime(message.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}

                <div ref={messagesEndRef} />
              </div>
            </CardContent>

            {/* Template suggestors (conditional) */}
            {selectedConversation?.channel?.channel_type === 'whatsapp' && (
              <div className="px-4 py-1.5 border-t border-border bg-card">
                <TemplateSuggestor
                  conversationId={selectedConversation.id}
                  contactPhone={selectedConversation.contact?.phone_number}
                  contact={selectedConversation?.contact}
                  refreshTrigger={templateSuggestorRefreshKey}
                  onSelectTemplate={(template, variables) => {
                    setSelectedTemplate({ id: template.id, name: template.name, variables });
                    const bodyText = template.components?.body;
                    if (bodyText) {
                      let preview = bodyText;
                      Object.entries(variables).forEach(([key, value]) => {
                        preview = preview.replace(`{{${key}}}`, value);
                      });
                      setMessageText(preview);
                    } else {
                      setMessageText(`[Template: ${template.name}]`);
                    }
                  }}
                  compact
                />
              </div>
            )}

            {selectedConversation?.channel?.channel_type === 'email' && (
              <div className="px-4 py-2 border-t border-border bg-muted/20">
                <EmailTemplateSuggestor
                  conversationId={selectedConversation.id}
                  onSent={() => { loadMessages(); }}
                />
              </div>
            )}

            {/* CardFooter — Rich Text Editor */}
            <CardFooter className="p-0 block border-t border-border bg-card">
              <RichTextEditor
                value={messageText}
                onChange={handleMessageTextChange}
                onSend={handleSendMessage}
                onAttachFile={handleFileAttachment}
                onStartRecording={() => setVoiceRecorderOpen(true)}
                onStartSpeechToText={() => setSpeechToTextDialogOpen(true)}
                onOpenSignatureDialog={() => setSignatureDialogOpen(true)}
                placeholder="Type your message…"
                disabled={loading || uploading}
                showSignatureButton={true}
              />
            </CardFooter>
          </Card>

          {/* Co-Pilot Panel */}
          {copilotEnabled && (
            <div className="w-[420px] shrink-0">
              <CoPilotPanel conversationId={selectedConversation.id} />
            </div>
          )}
        </div>
      ) : (
        /* ── Empty state ───────────────────────────────────────────────────── */
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 bg-muted/20 rounded-none rounded-r-lg p-6">

          {/* Icon stack */}
          <div className="relative">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-background border border-border shadow-sm">
              <MessageSquarePlus className="h-8 w-8 text-primary/70" />
            </div>
            <div className="absolute -bottom-1 -right-1 h-6 w-6 flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
              <MessageSquare className="h-3 w-3" />
            </div>
          </div>

          {/* Text */}
          <div className="text-center space-y-2">
            <h2 className="text-lg font-semibold tracking-tight">No conversation selected</h2>
            <p className="text-sm text-muted-foreground max-w-[260px] leading-relaxed">
              Pick a conversation from the list, or start a new one to connect with your customers.
            </p>
          </div>

          {/* CTA */}
          <Button className="gap-2 shadow-sm" onClick={() => setConversationInitiatorOpen(true)}>
            <MessageSquarePlus className="h-4 w-4" />
            Start New Conversation
          </Button>

        </div>
      )}

      {/* ── Dialogs ───────────────────────────────────────────────────────── */}

      {/* Download Menu — shadcn DropdownMenu rendered inline (no anchor state needed) */}

      {/* Assignment Dialog */}
      <AssignmentDialog
        open={assignmentDialogOpen}
        onClose={() => setAssignmentDialogOpen(false)}
        conversationId={selectedConversation?.id || 0}
        currentAssignee={selectedConversation?.assignee}
        onAssigned={handleAssignmentComplete}
      />

      <LabelsDialog
        open={labelsDialogOpen}
        onClose={() => setLabelsDialogOpen(false)}
        conversationId={selectedConversation?.id || 0}
        currentLabels={selectedConversation?.labels || []}
        onLabelsUpdated={handleLabelsUpdated}
      />

      <VoiceRecorder
        open={voiceRecorderOpen}
        onClose={() => setVoiceRecorderOpen(false)}
        onSendRecording={handleVoiceRecording}
      />

      <SpeechToTextDialog
        open={speechToTextDialogOpen}
        onClose={() => setSpeechToTextDialogOpen(false)}
        onTranscriptComplete={handleSpeechToTextComplete}
      />

      <SignatureDialog
        open={signatureDialogOpen}
        onClose={() => setSignatureDialogOpen(false)}
        onSaveSignature={handleSignatureSave}
        currentSignature={userSignature}
        signatureEnabled={signatureEnabled}
      />

      <ParticipantsDialog
        open={participantsDialogOpen}
        onClose={() => setParticipantsDialogOpen(false)}
        conversationId={selectedConversation?.id || 0}
        currentParticipants={participants}
        onParticipantsUpdated={handleParticipantsUpdated}
      />

      {selectedConversation && (
        <ConversationMetrics
          open={metricsDialogOpen}
          onClose={() => setMetricsDialogOpen(false)}
          conversationId={selectedConversation.id}
          conversation={selectedConversation}
        />
      )}

      <ConversationInitiator
        open={conversationInitiatorOpen}
        onClose={() => setConversationInitiatorOpen(false)}
        onConversationCreated={(newConversationId) => {
          loadConversations();
          window.location.href = `/conversations/${newConversationId}`;
        }}
      />

    </div>
  );
};

export default ConversationsPage;
